import {Color} from '@benchling/nucleus-ui';

import {AntibodyProps} from 'antibodies/Antibody';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';

export const ANTIBODY_DOMAIN_CORNER_RADIUS = 4;
export const ANTIBODY_DOMAIN_HEIGHT = 64;
export const ANTIBODY_DOMAIN_STROKE_THICKNESS = 3;
export const ANTIBODY_DOMAIN_WIDTH = 32;
export const SVG_VIEWBOX_WIDTH = 138;
export const SVG_VIEWBOX_HEIGHT = 138;

// TODO: Map by named specificity instead of indexing into the alphabet
// TODO: Fill in eight colors per domain type
export const COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY: Record<AntibodyDomainType, Array<string>> = {
  [AntibodyDomainType.VL]: [Color.LIME_400, Color.ORANGE_400],
  [AntibodyDomainType.CL]: [Color.LIME_200, Color.ORANGE_200],
  [AntibodyDomainType.VH]: [Color.BLUE_350, Color.PURPLE_400],
  [AntibodyDomainType.VHH]: [Color.BLUE_400],
  [AntibodyDomainType.CH1]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.CH2]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.CH3]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.CH4]: [Color.BLUE_200, Color.PURPLE_200],
  [AntibodyDomainType.J]: [Color.BLACK, Color.BLACK],
  [AntibodyDomainType.H]: [Color.BLACK, Color.BLACK],
  [AntibodyDomainType.L]: [Color.BLACK, Color.BLACK],
  [AntibodyDomainType.VA]: [Color.BLUE_350],
  [AntibodyDomainType.CA]: [Color.BLUE_200],
  [AntibodyDomainType.VB]: [Color.LIME_400],
  [AntibodyDomainType.CB]: [Color.LIME_200],
  [AntibodyDomainType.VG]: [Color.ORANGE_400],
  [AntibodyDomainType.CG]: [Color.ORANGE_200],
  [AntibodyDomainType.VD]: [Color.PURPLE_400],
  [AntibodyDomainType.CD]: [Color.PURPLE_200],
};

// TODO: what are the technical terms here?
export enum TerminusStyle {
  FLAT = 'FLAT',
  NOTCH = 'NOTCH',
}

// Copy/pasted out of Figma SVG. TODO: Calculate these from chains / interactions... or persist them
export const IGG_REST_PROPS: Omit<AntibodyProps, 'format'> = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VH,
      translate: {x: 35.2754, y: 28.5516},
      rotate: -35,
    },
    2: {
      type: AntibodyDomainType.CH1,
      translate: {x: 46.2842, y: 43.9646},
      rotate: -35,
    },
    3: {
      type: AntibodyDomainType.H,
      angle: 145,
      freeEndLength: 4,
      fixedEndLength: 8,
      translate: {x: 60.6807, y: 56.0275},
    },
    4: {
      type: AntibodyDomainType.CH2,
      translate: {x: 57.7451, y: 65.5688},
    },
    5: {
      type: AntibodyDomainType.CH3,
      translate: {x: 57.7451, y: 84.6514},
    },
    6: {
      type: AntibodyDomainType.VL,
      translate: {x: 25.0, y: 35.8912},
      rotate: -35,
    },
    7: {
      type: AntibodyDomainType.CL,
      translate: {x: 36.0098, y: 51.3041},
      rotate: -35,
    },
    8: {
      type: AntibodyDomainType.VH,
      translate: {x: 94.6914, y: 23.0},
      rotate: 35,
    },
    9: {
      type: AntibodyDomainType.CH1,
      translate: {x: 83.9648, y: 38.4919},
      rotate: 35,
    },
    10: {
      type: AntibodyDomainType.H,
      angle: 145,
      freeEndLength: 4,
      fixedEndLength: 8,
      translate: {x: 77.56, y: 56.03},
      reflect: true,
    },
    11: {
      type: AntibodyDomainType.CH2,
      translate: {x: 70.9561, y: 65.5688},
    },
    12: {
      type: AntibodyDomainType.CH3,
      translate: {x: 70.9561, y: 84.6514},
    },
    13: {
      type: AntibodyDomainType.VL,
      translate: {x: 105.7, y: 30.3394},
      rotate: 35,
    },
    14: {
      type: AntibodyDomainType.CL,
      translate: {x: 94.9736, y: 45.8313},
      rotate: 35,
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 1.80392,
      height: 18.3486,
      width: 9.54128,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: SVG_VIEWBOX_HEIGHT,
      svgViewboxWidth: SVG_VIEWBOX_WIDTH,
    },
  },
};

export const NANOBODY_REST_PROPS: Omit<AntibodyProps, 'format'> = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VHH,
      translate: {x: 68.95, y: 29.73},
      rotate: 30,
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 4,
      height: 77.64,
      width: 38.82,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: SVG_VIEWBOX_HEIGHT,
      svgViewboxWidth: SVG_VIEWBOX_WIDTH,
    },
  },
};

export const SCFV_REST_PROPS: Omit<AntibodyProps, 'format'> = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VH,
      rotate: 30,
      translate: {x: 56.44, y: 34.04},
      nTerminusStyle: TerminusStyle.NOTCH,
    },
    2: {
      type: AntibodyDomainType.L,
      rotate: 30,
      translate: {x: 70.66, y: 32.87},
      centerLength: 49.49,
      endRadius: 8.57,
      tipLength: 8,
    },
    3: {
      type: AntibodyDomainType.VL,
      rotate: 30,
      translate: {x: 86.41, y: 51.56},
      nTerminusStyle: TerminusStyle.NOTCH,
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 4,
      height: 49.49,
      width: 22.57,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: SVG_VIEWBOX_HEIGHT,
      svgViewboxWidth: SVG_VIEWBOX_WIDTH,
    },
  },
};

export const FAB_REST_PROPS: Omit<AntibodyProps, 'format'> = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VH,
      rotate: 30,
      translate: {x: 68.95, y: 29.73},
    },
    2: {
      type: AntibodyDomainType.CH1,
      rotate: 30,
      translate: {x: 51.62, y: 60},
    },
    3: {
      type: AntibodyDomainType.CL,
      rotate: 30,
      translate: {x: 89.08, y: 40.5},
    },
    4: {
      type: AntibodyDomainType.CL,
      rotate: 30,
      translate: {x: 71.62, y: 70},
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 2.68,
      height: 33.24,
      width: 15.16,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: SVG_VIEWBOX_HEIGHT,
      svgViewboxWidth: SVG_VIEWBOX_WIDTH,
    },
  },
};

export const TCR_REST_PROPS: Omit<AntibodyProps, 'format'> = {
  displayByRef: {
    1: {
      type: AntibodyDomainType.VA,
      nTerminusStyle: TerminusStyle.NOTCH,
      translate: {x: 40, y: 20},
    },
    2: {
      type: AntibodyDomainType.CA,
      translate: {x: 40, y: 72},
    },
    3: {
      type: AntibodyDomainType.VB,
      nTerminusStyle: TerminusStyle.NOTCH,
      translate: {x: 76, y: 20},
    },
    4: {
      type: AntibodyDomainType.CB,
      translate: {x: 76, y: 72},
    },
  },
  sizing: {
    chainDomainSizing: {
      cornerRadius: 4,
      height: 49.49,
      width: 22.57,
    },
    connectorSizing: {},
    svgSizing: {
      svgViewboxHeight: SVG_VIEWBOX_HEIGHT,
      svgViewboxWidth: SVG_VIEWBOX_WIDTH,
    },
  },
};

export const ANTIBODY_REST_PROPS_BY_ID: Record<string, Omit<AntibodyProps, 'format'>> = {
  bnchfmt_IgGmono: IGG_REST_PROPS,
  bnchfmt_IgGbi: IGG_REST_PROPS,
  bnchfmt_nano: NANOBODY_REST_PROPS,
  bnchfmt_scFv: SCFV_REST_PROPS,
  bnchfmt_Fab: FAB_REST_PROPS,
  bnchfmt_TCR: TCR_REST_PROPS,
};
